import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Package, Loader2 } from 'lucide-react';
import './TrackingDetails.css';
import { ORDER_STATUS } from "../utils/orderStatus";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const TrackingDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromPath = location.state?.from || '/home';
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchMyOrders();
  }, []);

  const fetchMyOrders = async () => {
    try {
      setLoading(true);
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        setError('Please log in to view your orders.');
        setLoading(false);
        return;
      }
      
      const user = JSON.parse(userStr);
      
      const res = await fetch(`${API_URL}/orders/user/${user.id}`);
      const data = await res.json();
      
      if (data.success) {
        // Fetch items for each order to show the thumbnail and item count
        const allOrders = data.data;
        const ordersWithItems = await Promise.all(allOrders.map(async (order) => {
            const itemRes = await fetch(`${API_URL}/orders/${order.id}`);
            const itemData = await itemRes.json();
            if(itemData.success) {
                 return itemData.data;
            }
            return order;
        }));
        setOrders(ordersWithItems);
      } else {
        setError(data.message || 'Failed to fetch orders');
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('An error occurred while loading your orders.');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: true
    });
  };



  return (
    <div className="orders-page-container fade-in">
      <div className="orders-top-nav">
        <button onClick={() => navigate(-1)} className="back-link">
          <ArrowLeft size={20} />
          <span>Back</span>
        </button>
      </div>
      
      <div className="orders-content">
        <div className="customer-orders-header">
            <Package size={28} className="orders-icon-pink" />
            <h1>My Orders</h1>
        </div>

        {loading ? (
            <div className="orders-loading-state">
                <Loader2 className="spin-icon" size={40} color="#db2777" />
            </div>
        ) : error ? (
            <div className="orders-error-state">
                <p>{error}</p>
                <button className="btn-start-shopping" onClick={() => navigate('/login')}>Go to Login</button>
            </div>
        ) : orders.length === 0 ? (
          <div className="orders-card empty-state-card">
            <div className="empty-state-container">
              <Package size={80} className="empty-state-icon" />
              <h3>No orders yet</h3>
              <p>Start shopping to see your orders here!</p>
              <button className="btn-start-shopping" onClick={() => navigate('/home')}>
                Start Shopping
              </button>
            </div>
          </div>
        ) : (
            <div className="customer-orders-list">
                {orders.map(order => (
                    <div
                      key={order.id}
                      className={`customer-order-card fade-in ${order.status === "cancelled" ? "disabled-card" : ""}`}
                      onClick={() => {
                        if (order.status !== "cancelled") {
                          navigate(`/my-orders/${order.id}`);
                        }
                      }}
                    >
                        <div className="customer-order-top">
                            <div className="co-header-left">
                                <h3>Order ID: #{order.id.split('-')[0]}</h3>
                                <p className="co-date">{formatDateTime(order.created_at)}</p>
                            </div>
                            <div className="co-header-right">
                                <p className="co-total-label">Total</p>
                                <p className="co-total-amount">₹{Number(order.total_amount).toLocaleString('en-IN')}</p>
                            </div>
                        </div>

                        <div className="co-middle">

<span className={`order-status-badge ${ORDER_STATUS[order.status]?.className}`}>
    {ORDER_STATUS[order.status]?.label}
</span>
                        </div>

                        {order.items && order.items.length > 0 && (
                            <div className="co-bottom">
                                <img 
                                    src={order.items[0].product_image || '/uploads/placeholder.jpg'} 
                                    alt={order.items[0].product_name} 
                                    className="co-item-thumb"
                                    onError={(e) => { e.target.src = '/uploads/placeholder.jpg'; }}
                                />
                                <p className="co-items-text">
                                  {order.status === "cancelled" ? (
                                    "Your order is cancelled. Please contact Ithal by MR for more details."
                                  ) : (
                                    `${order.items.length} item${order.items.length > 1 ? 's' : ''} • Click to view details`
                                  )}
                                </p>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        )}
      </div>
    </div>
  );
};

export default TrackingDetails;
